/*
 * Ticket types and internal tickets (support-module.md §10).
 *
 * Every company on DeeCRM360 can run its OWN internal tickets: staff raise them
 * from the CRM, and a team works them through the phases the company defines
 * per TICKET TYPE. Customer support (tickets from HRM / LMS tenants through
 * the Support API) is just one more type, of kind "customer".
 *
 * A type carries its own phases (kinds open / done / rejected / paused), the
 * moves allowed between them and the fields a move needs, its form fields,
 * categories, SLA per priority, default team, who may raise it and who may see
 * the requests. Sample company: Demulla Investment Ltd.
 */
import { reactive } from "vue";
import { hoursAgo, slaHours, ticketCategories } from "@/mock/support";
import { staff, teams, departments } from "@/mock/masters";

const addHours = (iso, h) => new Date(new Date(iso).getTime() + h * 3600000).toISOString();

// The signed-in requester in the prototype (Jane Wanjiku, Sales Manager).
export const ME_REQUESTER = 1;

// A staff member's department, through their team (Finance has no team).
export const staffDepartmentId = (id) => {
  const s = staff.find((x) => x.id === Number(id));
  if (!s) return null;
  return teams.find((t) => t.id === s.team_id)?.department_id ?? (s.role === "Finance" ? 6 : null);
};
export const departmentName = (id) => departments.find((d) => d.id === Number(id))?.name ?? "—";

/* ---------- Ticket types ---------- */

// Phase kinds: what the engine relies on, whatever the phase is called.
export const phaseKinds = [
  { key: "open", label: "Open", tone: "primary" },
  { key: "paused", label: "Paused (SLA stops)", tone: "warning" },
  { key: "done", label: "Done", tone: "success" },
  { key: "rejected", label: "Rejected", tone: "danger" },
];
export const phaseTone = (kind) => phaseKinds.find((k) => k.key === kind)?.tone ?? "secondary";

export const fieldTypes = [
  { key: "text", label: "Text" },
  { key: "number", label: "Number" },
  { key: "date", label: "Date" },
  { key: "dropdown", label: "Dropdown" },
  { key: "phone", label: "Phone" },
  { key: "currency", label: "Currency (KES)" },
  { key: "yesno", label: "Yes / no" },
];

// Teams that work tickets; members are the people the queue can assign to.
export const workTeams = [
  { key: "service_desk", name: "Service Desk", members: [8, 10, 7] },
  { key: "welfare_desk", name: "Customer Welfare Desk", members: [10, 7] },
  { key: "operations", name: "Operations & Admin", members: [6, 9] },
  { key: "it_helpdesk", name: "IT Helpdesk", members: [8, 6] },
  { key: "finance", name: "Finance", members: [11] },
];
export const workTeam = (key) => workTeams.find((t) => t.key === key);

export const raiseRoles = ["Head of Sales", "Sales Manager", "Sales", "Customer Success", "Support", "Implementation", "Marketing", "Finance"];
export const typeIcons = ["iconoir-heart", "iconoir-tools", "iconoir-laptop", "iconoir-wallet", "iconoir-headset", "iconoir-truck", "iconoir-building", "iconoir-hand-cash", "iconoir-community", "iconoir-shield-check"];
export const typeTones = ["primary", "success", "info", "warning", "danger", "purple", "secondary"];

const P = (key, name, kind, max_days = null) => ({ key, name, kind, max_days });
const F = (key, label, type, extra = {}) => ({ key, label, type, options: [], required: false, sensitive: false, help: "", ...extra });
// SLA per priority in hours: [response, resolution].
const SLA = (c, h, m, l) => ({ critical: c, high: h, medium: m, low: l });

export const ticketTypes = reactive([
  {
    id: 1,
    key: "customer_support",
    name: "Customer Support",
    kind: "customer",
    prefix: "TKT",
    icon: "iconoir-headset",
    tone: "primary",
    description: "Tickets from Demulla's clients: the HRM / LMS tenants through the Support API, the portal, email and phone.",
    default_team: "service_desk",
    default_priority: "medium",
    raise: { mode: "roles", roles: ["Support", "Customer Success"] },
    visibility: "department",
    active: true,
    system: true,
    phases: [
      P("new", "New", "open", 1), P("open", "Open", "open", 2), P("in_progress", "In progress", "open", 5), P("awaiting_customer", "Awaiting customer", "paused", 7),
      P("reopened", "Reopened", "open", 2), P("resolved", "Resolved", "done"), P("closed", "Closed", "done"),
    ],
    transitions: {
      new: ["open", "in_progress", "closed"], open: ["in_progress", "awaiting_customer", "resolved"], in_progress: ["awaiting_customer", "resolved"],
      awaiting_customer: ["in_progress", "resolved", "closed"], reopened: ["in_progress", "resolved"], resolved: ["closed", "reopened"], closed: ["reopened"],
    },
    required: { "in_progress>resolved": ["Resolution summary", "Root cause"], "open>resolved": ["Resolution summary", "Root cause"], "resolved>reopened": ["Reason for reopening"] },
    fields: [],
    categories: ticketCategories.slice(),
    sla: SLA(...Object.values(slaHours)),
  },
  {
    id: 2,
    key: "loan_welfare",
    name: "Loan Customer Welfare",
    kind: "internal",
    prefix: "WEL",
    icon: "iconoir-heart",
    tone: "danger",
    description: "Cases about Demulla's own borrowers: bereavement, hardship, complaints and restructure requests raised by branch and field staff.",
    default_team: "welfare_desk",
    default_priority: "high",
    raise: { mode: "roles", roles: ["Head of Sales", "Sales Manager", "Sales", "Customer Success"] },
    visibility: "department",
    reveal_roles: ["Customer Welfare Desk", "Head of Credit"],
    active: true,
    phases: [
      P("logged", "Logged", "open", 1), P("contacting", "Contacting customer", "open", 2), P("review", "Under review", "open", 5),
      P("action", "Action taken", "open", 5), P("closed", "Closed", "done"), P("rejected", "Rejected", "rejected"),
    ],
    transitions: { logged: ["contacting", "rejected"], contacting: ["review", "rejected"], review: ["action", "contacting", "rejected"], action: ["closed", "review"] },
    required: {
      "contacting>review": ["Contact outcome"],
      "review>action": ["Decision", "Approved by"],
      "action>closed": ["Action taken summary"],
      "logged>rejected": ["Rejection reason"],
      "contacting>rejected": ["Rejection reason"],
      "review>rejected": ["Rejection reason"],
    },
    fields: [
      F("borrower_name", "Borrower name", "text", { required: true }),
      F("borrower_phone", "Borrower phone", "phone", { required: true, help: "Safaricom or Airtel number the borrower answers" }),
      F("national_id", "National ID", "text", { required: true, sensitive: true, help: "Masked after saving; only the welfare desk can reveal it" }),
      F("loan_number", "Loan number", "text", { required: true, help: "e.g. LN-20931" }),
      F("branch", "Branch", "dropdown", { required: true, options: ["Kakamega", "Bungoma", "Kisumu", "Nairobi"] }),
      F("welfare_category", "Welfare category", "dropdown", { required: true, options: ["Bereavement", "Hardship", "Complaint", "Restructure request", "Other"] }),
      F("outstanding", "Outstanding balance", "currency", { help: "From the LMS loan statement" }),
    ],
    categories: ["Borrower welfare", "Collections conduct", "Insurance claim", "Account query"],
    sla: SLA([2, 24], [4, 48], [8, 72], [24, 120]),
  },
  {
    id: 3,
    key: "operations",
    name: "Operations",
    kind: "internal",
    prefix: "OPS",
    icon: "iconoir-tools",
    tone: "success",
    description: "Facilities, transport, supplies and security requests for head office and the branches.",
    default_team: "operations",
    default_priority: "medium",
    raise: { mode: "everyone", roles: [] },
    visibility: "own",
    active: true,
    phases: [P("logged", "Logged", "open", 1), P("assigned", "Assigned", "open", 2), P("in_progress", "In progress", "open", 5), P("done", "Done", "done"), P("cancelled", "Cancelled", "rejected")],
    transitions: { logged: ["assigned", "cancelled"], assigned: ["in_progress", "cancelled"], in_progress: ["done", "assigned"] },
    required: { "in_progress>done": ["Work done"], "logged>cancelled": ["Reason"], "assigned>cancelled": ["Reason"] },
    fields: [
      F("department", "Department", "dropdown", { required: true, options: departments.map((d) => d.name) }),
      F("location", "Location", "dropdown", { required: true, options: ["Head office – Upper Hill", "Nairobi CBD branch", "Kakamega branch", "Bungoma branch", "Kisumu branch"] }),
      F("urgency", "Urgency", "dropdown", { required: true, options: ["Today", "This week", "When possible"] }),
    ],
    categories: ["Facilities / repairs", "Office supplies", "Transport / logistics", "Security", "Cleaning"],
    sla: SLA([1, 8], [4, 24], [8, 72], [24, 168]),
  },
  {
    id: 4,
    key: "it_support",
    name: "IT Support",
    kind: "internal",
    prefix: "IT",
    icon: "iconoir-laptop",
    tone: "info",
    description: "Laptops, printers, email, network and access to Demulla's systems (CRM, LMS, HRM).",
    default_team: "it_helpdesk",
    default_priority: "medium",
    raise: { mode: "everyone", roles: [] },
    visibility: "own",
    active: true,
    phases: [
      P("logged", "Logged", "open", 1), P("assessing", "Assessing", "open", 1), P("fixing", "Fixing", "open", 3),
      P("awaiting", "Awaiting requester", "paused", 3), P("done", "Done", "done"),
    ],
    transitions: { logged: ["assessing"], assessing: ["fixing", "awaiting", "done"], fixing: ["awaiting", "done"], awaiting: ["fixing", "done"], done: ["fixing"] },
    required: { "assessing>done": ["Resolution"], "fixing>done": ["Resolution"], "awaiting>done": ["Resolution"], "done>fixing": ["Why reopened"] },
    fields: [
      F("asset", "Asset tag", "text", { help: "On the sticker, e.g. DML-LT-0142" }),
      F("system", "System affected", "dropdown", { required: true, options: ["Email / Microsoft 365", "DeeCRM360", "Demulla LMS", "DeeHr360 HRM", "Network / Wi-Fi", "Printer", "Laptop / desktop", "Phone"] }),
    ],
    categories: ["Hardware", "Software", "Access / password", "Network", "New equipment"],
    sla: SLA([1, 4], [2, 8], [4, 24], [8, 72]),
  },
  {
    id: 5,
    key: "finance_request",
    name: "Finance Request",
    kind: "internal",
    prefix: "FIN",
    icon: "iconoir-wallet",
    tone: "warning",
    description: "Petty cash, supplier payments, staff advances and refunds, reviewed and approved before payment.",
    default_team: "finance",
    default_priority: "medium",
    raise: { mode: "everyone", roles: [] },
    visibility: "department",
    active: true,
    phases: [P("logged", "Logged", "open", 1), P("review", "Review", "open", 2), P("approval", "Approval", "open", 2), P("paid", "Paid / Done", "done"), P("rejected", "Rejected", "rejected")],
    transitions: { logged: ["review", "rejected"], review: ["approval", "rejected"], approval: ["paid", "review", "rejected"] },
    required: { "review>approval": ["Budget line confirmed"], "approval>paid": ["Payment reference"], "logged>rejected": ["Rejection reason"], "review>rejected": ["Rejection reason"], "approval>rejected": ["Rejection reason"] },
    fields: [
      F("amount", "Amount", "currency", { required: true }),
      F("cost_centre", "Cost centre", "dropdown", { required: true, options: ["SAL-NBI", "SAL-WST", "CS-HQ", "SUP-HQ", "OPS-HQ", "MKT-HQ", "FIN-HQ"] }),
      F("payee", "Payee", "text", { help: "Supplier or staff member to be paid" }),
      F("needed_by", "Needed by", "date"),
      F("receipt", "Receipt / invoice attached", "yesno"),
    ],
    categories: ["Petty cash", "Supplier payment", "Staff advance", "Travel & per diem", "Refund"],
    sla: SLA([2, 8], [4, 24], [8, 48], [16, 120]),
  },
]);

export const ticketTypeByKey = (key) => ticketTypes.find((t) => t.key === key);
export const phaseOf = (type, key) => type?.phases.find((p) => p.key === key) ?? { key, name: key, kind: "open" };
// StageTracker speaks open / won / lost / paused: done maps to won, rejected to lost.
export const trackerPhases = (type) => (type?.phases ?? []).map((p, i) => ({ ...p, order: i + 1, kind: { done: "won", rejected: "lost" }[p.kind] ?? p.kind }));
// The path a requester sees: open/paused phases then the first done phase.
export const phasePath = (type) => [...type.phases.filter((p) => p.kind === "open" || p.kind === "paused"), type.phases.find((p) => p.kind === "done")].filter(Boolean);
export const canRaise = (type, role) => type.active && type.kind === "internal" && (type.raise.mode === "everyone" || type.raise.roles.includes(role));
export const slaSummary = (type) => `High ${type.sla.high[0]}h / ${type.sla.high[1]}h`;
// "1 hour", "3 days": an SLA target in words, counting 8 working hours a day.
export const hoursLabel = (h) => (h < 8 ? `${h} hour${h === 1 ? "" : "s"}` : `${+(h / 8).toFixed(1)} working day${h === 8 ? "" : "s"}`);

// Status the shared SLA helpers understand, from a phase's kind.
export const statusForPhase = (type, key) => {
  const p = phaseOf(type, key);
  if (p.kind === "paused") return "awaiting_customer";
  if (p.kind === "done") return "resolved";
  if (p.kind === "rejected") return "closed";
  return type.phases[0]?.key === key ? "new" : "in_progress";
};

// "•••• 4471": how a sensitive value shows until someone permitted reveals it.
export const maskValue = (v) => (v ? `•••• ${String(v).slice(-4)}` : "—");
export const formatValue = (field, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (field.type === "currency") return `KES ${Number(v).toLocaleString()}`;
  if (field.type === "yesno") return v ? "Yes" : "No";
  return String(v);
};

/* ---------- Internal tickets ---------- */

const counters = { WEL: 4, OPS: 23, IT: 39, FIN: 14 };
export const nextNumber = (type) => {
  counters[type.prefix] = (counters[type.prefix] ?? 0) + 1;
  return `${type.prefix}-${String(counters[type.prefix]).padStart(4, "0")}`;
};

/*
 * [type, subject, requester, priority, trail of phases, owner, created hours ago,
 *  hours since last move, category, field values, extra]
 * The trail is the phases the ticket has been through, current one last.
 */
const ROWS = [
  // Loan Customer Welfare
  ["loan_welfare", "Borrower's husband passed away — request to pause repayments", 4, "high", ["logged", "contacting", "review"], 10, 70, 30, "Borrower welfare",
    { borrower_name: "Mary Nafula", borrower_phone: "+254 712 448 109", national_id: "28419471", loan_number: "LN-20931", branch: "Bungoma", welfare_category: "Bereavement", outstanding: 184500 }],
  ["loan_welfare", "Hospitalised borrower missed two instalments", 1, "high", ["logged", "contacting"], 10, 30, 26, "Borrower welfare",
    { borrower_name: "Wycliffe Wanyama", borrower_phone: "+254 723 901 554", national_id: "31877206", loan_number: "LN-21408", branch: "Kakamega", welfare_category: "Hardship", outstanding: 96200 }, { awaiting: false }],
  ["loan_welfare", "Borrower complains of rude collections call", 4, "medium", ["logged", "contacting", "review", "action", "closed"], 7, 300, 40, "Collections conduct",
    { borrower_name: "Josephine Akinyi", borrower_phone: "+254 701 337 820", national_id: "25560913", loan_number: "LN-19877", branch: "Kisumu", welfare_category: "Complaint", outstanding: 41000 }, { rating: 5, confirmed: true }],
  ["loan_welfare", "Restructure request after shop fire in Kakamega market", 5, "high", ["logged", "contacting", "review", "action"], 7, 150, 20, "Borrower welfare",
    { borrower_name: "Hesbon Barasa", borrower_phone: "+254 722 615 004", national_id: "29934518", loan_number: "LN-20117", branch: "Kakamega", welfare_category: "Restructure request", outstanding: 322000 }],
  ["loan_welfare", "Credit-life claim for deceased borrower not processed", 3, "critical", ["logged"], null, 3, 3, "Insurance claim",
    { borrower_name: "Caroline Wekesa", borrower_phone: "+254 710 220 671", national_id: "30451172", loan_number: "LN-21890", branch: "Bungoma", welfare_category: "Bereavement", outstanding: 210000 }],
  ["loan_welfare", "Borrower disputes penalty after M-Pesa reversal", 1, "medium", ["logged", "contacting", "rejected"], 10, 200, 120, "Account query",
    { borrower_name: "Patrick Simiyu", borrower_phone: "+254 733 118 402", national_id: "27788310", loan_number: "LN-18452", branch: "Nairobi", welfare_category: "Complaint", outstanding: 15300 }, { reason: "Penalty was correct: the reversal was requested by the borrower, not a system fault. Explained to him by phone." }],
  ["loan_welfare", "Elderly borrower asks to move to monthly instalments", 5, "low", ["logged", "contacting", "review"], 10, 120, 110, "Borrower welfare",
    { borrower_name: "Dorcas Atieno", borrower_phone: "+254 714 556 930", national_id: "12093384", loan_number: "LN-17306", branch: "Kisumu", welfare_category: "Restructure request", outstanding: 58700 }],
  ["loan_welfare", "Flood-affected group in Budalang'i asks for grace period", 4, "high", ["logged", "contacting"], 7, 20, 18, "Borrower welfare",
    { borrower_name: "Rose Njeri (group chair)", borrower_phone: "+254 790 334 818", national_id: "33102457", loan_number: "LN-21655", branch: "Bungoma", welfare_category: "Hardship", outstanding: 450000 }],
  // Operations
  ["operations", "Air conditioner leaking in the Kisumu branch banking hall", 4, "medium", ["logged", "assigned", "in_progress"], 6, 50, 20, "Facilities / repairs", { department: "Sales", location: "Kisumu branch", urgency: "This week" }],
  ["operations", "Branded folders for the Kakamega borrower forum", 1, "low", ["logged", "assigned"], 9, 40, 30, "Office supplies", { department: "Sales", location: "Kakamega branch", urgency: "When possible" }],
  ["operations", "Vehicle for field visits to Bungoma groups on Thursday", 1, "high", ["logged", "assigned", "in_progress", "done"], 6, 96, 20, "Transport / logistics", { department: "Sales", location: "Bungoma branch", urgency: "This week" }],
  ["operations", "Access card for new support agent", 8, "medium", ["logged"], null, 5, 5, "Security", { department: "Support", location: "Head office – Upper Hill", urgency: "Today" }],
  ["operations", "Washroom repairs, 3rd floor", 9, "low", ["logged", "assigned", "in_progress"], 6, 260, 200, "Facilities / repairs", { department: "Marketing", location: "Head office – Upper Hill", urgency: "When possible" }],
  ["operations", "Extra chairs for the Nairobi CBD training room", 3, "low", ["logged", "cancelled"], 6, 180, 150, "Office supplies", { department: "Sales", location: "Nairobi CBD branch", urgency: "When possible" }, { reason: "Training moved to head office boardroom." }],
  // IT Support
  ["it_support", "Laptop will not charge", 1, "high", ["logged", "assessing", "awaiting"], 8, 26, 6, "Hardware", { asset: "DML-LT-0142", system: "Laptop / desktop" }, { awaiting: true }],
  ["it_support", "Cannot see Bungoma loans in the LMS after transfer", 4, "high", ["logged", "assessing", "fixing"], 8, 10, 4, "Access / password", { asset: "", system: "Demulla LMS" }],
  ["it_support", "Outlook keeps asking for password", 2, "medium", ["logged", "assessing", "fixing", "done"], 8, 80, 50, "Software", { asset: "DML-LT-0109", system: "Email / Microsoft 365" }, { rating: 4, confirmed: false }],
  ["it_support", "Kakamega branch Wi-Fi drops every afternoon", 5, "high", ["logged", "assessing"], 6, 8, 7, "Network", { asset: "DML-RT-0007", system: "Network / Wi-Fi" }],
  ["it_support", "New laptop for the incoming sales officer", 1, "medium", ["logged", "assessing", "fixing", "done"], 6, 220, 160, "New equipment", { asset: "DML-LT-0188", system: "Laptop / desktop" }, { rating: 5, confirmed: true }],
  ["it_support", "Printer at head office jams on every second page", 11, "low", ["logged"], null, 2, 2, "Hardware", { asset: "DML-PR-0003", system: "Printer" }],
  // Finance Request
  ["finance_request", "Fuel refund for Western region field trip", 1, "medium", ["logged", "review", "approval"], 11, 60, 22, "Travel & per diem", { amount: 14800, cost_centre: "SAL-WST", payee: "Jane Wanjiku", needed_by: "", receipt: true }],
  ["finance_request", "Petty cash for borrower forum refreshments", 4, "low", ["logged", "review", "approval", "paid"], 11, 170, 120, "Petty cash", { amount: 9500, cost_centre: "SAL-WST", payee: "Peter Ouma", needed_by: "", receipt: true }],
  ["finance_request", "Pay Bungoma venue hire invoice INV-2290", 3, "medium", ["logged", "review"], 11, 30, 26, "Supplier payment", { amount: 42000, cost_centre: "SAL-WST", payee: "Bungoma Tourist Hotel", needed_by: "", receipt: true }],
  ["finance_request", "Staff advance — school fees", 10, "medium", ["logged", "review", "rejected"], 11, 240, 200, "Staff advance", { amount: 60000, cost_centre: "CS-HQ", payee: "Samuel Kariuki", needed_by: "", receipt: false }, { reason: "Advance limit is one month's basic pay; resubmit for KES 45,000." }],
  ["finance_request", "Google Ads top-up for November campaign", 9, "high", ["logged"], null, 4, 4, "Supplier payment", { amount: 120000, cost_centre: "MKT-HQ", payee: "Google Ireland Ltd", needed_by: "", receipt: false }],
];

// Hand-written conversations for a few tickets; the rest get a generated one.
const THREADS = {
  WEL: [
    { v: "public", body: "Thanks for logging this. I'll call the borrower today and get back to you." },
    { v: "internal", body: "Checked the LMS: no arrears before this event. Credit-life cover is active on the loan." },
  ],
};
const DONE_NOTE = {
  done: "Done. Please confirm it is sorted, or reopen if not.",
  closed: "Case closed. Please confirm with the borrower and rate how we handled it.",
  paid: "Paid. M-Pesa B2C reference shared with the payee.",
};

export const internalTickets = reactive(
  ROWS.map(([type_key, subject, requester_id, priority, trail, owner_id, ago, sinceMove, category, values, extra = {}], i) => {
    const type = ticketTypeByKey(type_key);
    const counter = (counters[type.prefix] = (counters[type.prefix] ?? 0) + 1);
    const number = `${type.prefix}-${String(counter).padStart(4, "0")}`;
    const created_at = hoursAgo(ago);
    const phase = trail[trail.length - 1];
    const kind = phaseOf(type, phase).kind;
    const requester = staff.find((s) => s.id === requester_id);
    const worker = owner_id ? staff.find((s) => s.id === owner_id)?.name : "Queue";
    // Spread the earlier moves evenly between creation and the last move.
    const steps = trail.length - 1;
    const history = trail.map((p, n) => {
      const h = n === 0 ? ago : steps === 0 ? ago : ago - ((ago - sinceMove) * n) / steps;
      const ph = phaseOf(type, p);
      return {
        phase: p, at: hoursAgo(h), by: n === 0 ? requester.name : worker,
        note: n === 0 ? "Raised from the CRM" : ph.kind === "rejected" ? extra.reason : ph.kind === "done" ? DONE_NOTE[p] : (type.required[`${trail[n - 1]}>${p}`] ?? []).map((f) => `${f}: recorded`).join(" · ") || null,
      };
    });
    const [rH, resH] = type.sla[priority];
    const first_response_at = trail.length > 1 ? history[1].at : null;
    const resolved_at = kind === "done" || kind === "rejected" ? history[history.length - 1].at : null;
    const comments = [{ id: 1, visibility: "public", author_type: "customer", author: requester.name, at: created_at, body: `${subject}. ${values.borrower_name ? `Borrower ${values.borrower_name}, loan ${values.loan_number}.` : ""} Please assist.` }];
    if (first_response_at) {
      (type.prefix === "WEL" ? THREADS.WEL : [{ v: "public", body: "Picked up — on it." }]).forEach((c, n) =>
        comments.push({ id: comments.length + 1, visibility: c.v, author_type: "agent", author: worker, at: addHours(first_response_at, 0.1 * (n + 1)), body: c.body }));
    }
    if (extra.awaiting) comments.push({ id: comments.length + 1, visibility: "public", author_type: "agent", author: worker, at: hoursAgo(sinceMove), body: "Please bring the laptop and charger to the IT desk, 4th floor, so we can test the charging port. We'll swap the charger if that is the fault." });
    if (resolved_at) comments.push({ id: comments.length + 1, visibility: "public", author_type: "agent", author: worker, at: resolved_at, body: kind === "rejected" ? `Not taken forward: ${extra.reason}` : DONE_NOTE[phase] });
    if (extra.rating) comments.push({ id: comments.length + 1, visibility: "public", author_type: "feedback", author: requester.name, at: hoursAgo(Math.max(1, sinceMove - 4)), body: `Rated ${extra.rating}/5${extra.confirmed ? " and confirmed resolved" : ""}.` });
    return {
      id: 101 + i,
      number,
      type_key,
      kind: "internal",
      subject,
      requester_id,
      department_id: staffDepartmentId(requester_id),
      category,
      priority,
      phase,
      status: statusForPhase(type, phase),
      owner_id,
      team: workTeam(type.default_team)?.name,
      values: { ...values },
      history,
      comments,
      created_at,
      updated_at: [history[history.length - 1].at, ...comments.map((c) => c.at)].sort().pop(),
      first_response_at,
      response_due: addHours(created_at, rH),
      resolution_due: addHours(created_at, resH),
      resolved_at,
      reopen_count: 0,
      // Waiting on the requester: a paused phase, or done and not yet confirmed.
      awaiting_requester: kind === "paused" || (kind === "done" && !extra.confirmed),
      confirmed: !!extra.confirmed,
      rating: extra.rating ?? null,
      escalated: false,
    };
  }),
);

export const internalTicketById = (id) => internalTickets.find((t) => t.id === Number(id));
export const ticketsOfType = (key) => internalTickets.filter((t) => t.type_key === key);
export const isClosedPhase = (t) => ["done", "rejected"].includes(phaseOf(ticketTypeByKey(t.type_key), t.phase).kind);
export const daysInPhase = (t) => Math.max(0, Math.floor((Date.now() - new Date(t.history[t.history.length - 1].at).getTime()) / 86400000));
// Over the phase's "max days" (the ageing alert).
export const isAgeing = (t) => {
  const max = phaseOf(ticketTypeByKey(t.type_key), t.phase).max_days;
  return !!max && !isClosedPhase(t) && daysInPhase(t) > max;
};

/*
 * Move a ticket to another phase (the queue's "Move to"). The screen has
 * already checked the move is allowed and collected the required fields.
 */
export const moveTicket = (t, to, by, answers = {}) => {
  const type = ticketTypeByKey(t.type_key);
  const from = t.phase;
  const note = Object.entries(answers).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => `${k}: ${v}`).join(" · ") || null;
  t.history.push({ phase: to, at: new Date().toISOString(), by, note });
  t.phase = to;
  t.status = statusForPhase(type, to);
  const kind = phaseOf(type, to).kind;
  if (kind === "done" || kind === "rejected") t.resolved_at = new Date().toISOString();
  if (!t.first_response_at && from === type.phases[0].key) t.first_response_at = new Date().toISOString();
  t.awaiting_requester = kind === "paused" || kind === "done";
  t.updated_at = new Date().toISOString();
  return note;
};
